/**
 * Centralised copy for the auth magic-link flow. These strings were previously
 * duplicated verbatim across LoginPage / RegisterPage / AcceptInviteContent /
 * ReAuthModal — keeping them here means a copy change lands in one place and the
 * pages stay byte-for-byte identical to each other.
 */
export const AUTH_COPY = {
  sendFailed: 'Something went wrong. Please try again.',
  verifyFailed: 'Invalid or expired code. Please try again.',
  resendFailed: 'Failed to resend. Please try again.',
  codeResent: 'A new code has been sent to your inbox.',
  codeSent: (email: string) => `We sent a 6-digit code to ${email}`,
  resendCode: 'Resend code',
  resendCodeCountdown: (secondsRemaining: number) =>
    `Resend code in ${secondsRemaining}s`,
  passwordLoginFailed: 'That email or password is incorrect.',
  passwordResetRequestFailed: 'Something went wrong. Please try again.',
  passwordResetFailed:
    'This reset link is invalid or has expired. Please request a new one.',
  passwordsDoNotMatch: 'Those passwords do not match.',
  signupFailed: 'Invalid details for the account you are trying to access.',
  profileSaveFailed: 'Failed to save profile. Please try again.',
} as const;

/**
 * Which situation an auth error came from. The flow records it beside the
 * message so the card can give the banner the right title and next step —
 * the message string alone can't say (it may be the server's own text).
 */
export type AuthErrorKind =
  | 'generic'
  | 'link'
  | 'sendFailed'
  | 'verifyFailed'
  | 'resendFailed'
  | 'passwordLoginFailed'
  | 'signupFailed'
  | 'passwordsDoNotMatch';

export type AuthNoticeTone = 'success' | 'error' | 'warning';

export type AuthNoticeCopy = {
  tone: AuthNoticeTone;
  /** What happened, in a few words. */
  title: string;
  /** What to do — used when the server sent no message of its own. */
  body: string;
};

/**
 * Banner copy per situation. `fallback` is the string the flow stores when the
 * server sent no message: seeing it means "show our `body`"; any other string
 * is the server's own reason and is shown as the body instead, under our title.
 */
export const AUTH_ERROR_NOTICES: Record<
  Exclude<AuthErrorKind, 'generic' | 'link' | 'passwordsDoNotMatch'>,
  AuthNoticeCopy & { fallback: string }
> = {
  sendFailed: {
    tone: 'error',
    title: "We couldn't send your code",
    body: 'Something went wrong on our side. Please try again in a moment.',
    fallback: AUTH_COPY.sendFailed,
  },
  verifyFailed: {
    tone: 'error',
    title: "That code didn't work",
    body: 'It may be mistyped or expired. Check the newest email, or get a new code.',
    fallback: AUTH_COPY.verifyFailed,
  },
  resendFailed: {
    tone: 'error',
    title: "We couldn't send a new code",
    body: 'Wait a moment, then try Resend code again.',
    fallback: AUTH_COPY.resendFailed,
  },
  passwordLoginFailed: {
    tone: 'error',
    title: 'Email or password is incorrect',
    body: 'Check both and try again, or reset your password.',
    fallback: AUTH_COPY.passwordLoginFailed,
  },
  signupFailed: {
    tone: 'error',
    title: "We couldn't create your account",
    body: 'Check the email address and try again, or sign up with Google or Microsoft.',
    fallback: AUTH_COPY.signupFailed,
  },
};

/** "Something went wrong" + the server's text — for an error with no known situation. */
export const GENERIC_ERROR_TITLE = 'Something went wrong';

/** Inline error under the Confirm password field. */
export const PASSWORDS_DO_NOT_MATCH_INLINE =
  "Passwords don't match. Type the same password in both fields.";

/**
 * The `?error=` messages the callback / invite pages send the user back to
 * Login with, keyed by that exact text (each is written by one of those pages).
 * An unlisted message — a server error passed through — gets the generic title.
 */
export const LINK_ERROR_NOTICES: Record<string, AuthNoticeCopy> = {
  'OAuth sign-in failed. Please try again.': {
    tone: 'error',
    title: "Sign-in didn't finish",
    body: "Google or Microsoft didn't complete the sign-in. Try again, or use your email instead.",
  },
  'Missing OAuth token': {
    tone: 'error',
    title: "Sign-in didn't finish",
    body: "Google or Microsoft didn't complete the sign-in. Try again, or use your email instead.",
  },
  'Missing or invalid magic link token': {
    tone: 'error',
    title: "This sign-in link isn't valid",
    body: 'It may have expired. Enter your email below to get a new code.',
  },
  'Invalid or missing invite token': {
    tone: 'error',
    title: "This invite link isn't valid",
    body: 'Ask the person who invited you to send a new invite.',
  },
  'Invalid or missing invite link': {
    tone: 'error',
    title: "This invite link isn't valid",
    body: 'Ask the person who invited you to send a new invite.',
  },
  'You already belong to a different organization.': {
    tone: 'warning',
    title: "You're already in another organization",
    body: 'This account belongs to a different organization. Sign in with another email to accept this invite.',
  },
};

/** Title + body for an error the flow recorded, applying the server-text rule above. */
export function authErrorNotice(kind: AuthErrorKind, message: string): AuthNoticeCopy {
  if (kind === 'link') {
    return LINK_ERROR_NOTICES[message] ?? { tone: 'error', title: GENERIC_ERROR_TITLE, body: message };
  }
  if (kind === 'generic' || kind === 'passwordsDoNotMatch') {
    return { tone: 'error', title: GENERIC_ERROR_TITLE, body: message };
  }
  const notice = AUTH_ERROR_NOTICES[kind];
  return {
    tone: notice.tone,
    title: notice.title,
    body: message === notice.fallback ? notice.body : message,
  };
}

/**
 * Banner copy for the single-purpose pages (forgot / reset password, complete
 * profile). Same server-text rule as `AUTH_ERROR_NOTICES`.
 */
export const PAGE_ERROR_NOTICES = {
  resetRequestFailed: {
    tone: 'error',
    title: "We couldn't send the reset link",
    body: 'Something went wrong on our side. Please try again in a moment.',
    fallback: AUTH_COPY.passwordResetRequestFailed,
  },
  resetFailed: {
    tone: 'error',
    title: 'This reset link has expired',
    // The api's PASSWORD_RESET_EXPIRY_MINUTES (auth.service.ts) is 60.
    body: 'Reset links stop working after 60 minutes. Request a new one to continue.',
    fallback: AUTH_COPY.passwordResetFailed,
  },
  profileSaveFailed: {
    tone: 'error',
    title: "We couldn't save your name",
    body: 'Please try again in a moment.',
    fallback: AUTH_COPY.profileSaveFailed,
  },
} as const satisfies Record<string, AuthNoticeCopy & { fallback: string }>;

/** Title + body for one of the pages' errors: our sentence, or the server's. */
export function pageErrorNotice(
  key: keyof typeof PAGE_ERROR_NOTICES,
  message: string,
): AuthNoticeCopy {
  const notice = PAGE_ERROR_NOTICES[key];
  return {
    tone: notice.tone,
    title: notice.title,
    body: message === notice.fallback ? notice.body : message,
  };
}
